import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SidebarSettings {
  id: string;
  enable_sidebar: boolean;
  enable_sidebar_ads: boolean;
  sidebar_ad_position: 'top' | 'between' | 'bottom';
  sidebar_ad_html: string;
  enable_dont_miss: boolean;
  dont_miss_title: string;
  dont_miss_count: number;
  dont_miss_source: 'related' | 'popular' | 'latest';
}

const defaultSettings: SidebarSettings = {
  id: '',
  enable_sidebar: true,
  enable_sidebar_ads: true,
  sidebar_ad_position: 'top',
  sidebar_ad_html: '',
  enable_dont_miss: true,
  dont_miss_title: "Don't miss",
  dont_miss_count: 6,
  dont_miss_source: 'related',
};

export function useSidebarSettings() {
  return useQuery({
    queryKey: ['sidebar-settings'],
    queryFn: async (): Promise<SidebarSettings> => {
      const { data, error } = await supabase
        .from('sidebar_settings' as any)
        .select('*')
        .limit(1)
        .single();

      if (error || !data) return defaultSettings;

      const d = data as any;
      return {
        id: d.id,
        enable_sidebar: d.enable_sidebar ?? true,
        enable_sidebar_ads: d.enable_sidebar_ads ?? true,
        sidebar_ad_position: d.sidebar_ad_position ?? 'top',
        sidebar_ad_html: d.sidebar_ad_html ?? '',
        enable_dont_miss: d.enable_dont_miss ?? true,
        dont_miss_title: d.dont_miss_title ?? "Don't miss",
        dont_miss_count: d.dont_miss_count ?? 6,
        dont_miss_source: d.dont_miss_source ?? 'related',
      };
    },
    staleTime: 30_000,
  });
}

export function useSidebarSettingsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: Partial<SidebarSettings>) => {
      const { id, ...rest } = updates;
      const { error } = await supabase
        .from('sidebar_settings' as any)
        .update(rest as any)
        .eq('id', id as string);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sidebar-settings'] });
    },
  });
}
